import { http } from "@/lib/http-client";
import { unwrapData } from "@/lib/api-response";

import {
  AUTH_ENDPOINTS,
  ISSUE_CMS_HANDOFF_ENDPOINT,
  ISSUE_TICKETING_HANDOFF_ENDPOINT,
  SSO_EXCHANGE_ENDPOINT,
} from "@/constants/auth";
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from "@/types/auth";
import type { User } from "@/types/user";

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return http.post<LoginResponse>(AUTH_ENDPOINTS.LOGIN, credentials);
  },

  async resendOtp(email: string): Promise<{ message?: string }> {
    return http.post<{ message?: string }>(AUTH_ENDPOINTS.RESEND_OTP, { email });
  },

  async verifyOtp(payload: VerifyOtpRequest): Promise<VerifyOtpResponse> {
    return http.post<VerifyOtpResponse>(AUTH_ENDPOINTS.VERIFY_OTP, payload);
  },

  /** Exchange a one-time CMS SSO code for a dashboard session (same shape as verifyOtp). */
  async ssoExchange(code: string): Promise<VerifyOtpResponse> {
    return http.post<VerifyOtpResponse>(SSO_EXCHANGE_ENDPOINT, { code });
  },

  /** Mint a one-time CMS login URL for the current dashboard session. */
  async issueCmsHandoff(): Promise<{ code: string; cmsLoginUrl: string }> {
    const response = await http.post<{ code: string; cmsLoginUrl: string }>(
      ISSUE_CMS_HANDOFF_ENDPOINT,
      {},
    );
    return unwrapData(response);
  },

  /** Mint a one-time ticketing login URL for the current dashboard session. */
  async issueTicketingHandoff(): Promise<{ code: string; ticketingLoginUrl: string }> {
    const response = await http.post<{ code: string; ticketingLoginUrl: string }>(
      ISSUE_TICKETING_HANDOFF_ENDPOINT,
      {},
    );
    return unwrapData(response);
  },

  async getCurrentUser(): Promise<User> {
    const response = await http.get(AUTH_ENDPOINTS.ME);
    return unwrapData<User>(response);
  },

  async updateProfile(payload: {
    firstName?: string;
    lastName?: string;
  }): Promise<User> {
    const response = await http.patch(AUTH_ENDPOINTS.UPDATE_ME, payload);
    return unwrapData<User>(response);
  },

  async changePassword(payload: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    await http.post(AUTH_ENDPOINTS.CHANGE_PASSWORD, payload);
  },

  async completeInitialPassword(newPassword: string): Promise<User> {
    const response = await http.post(AUTH_ENDPOINTS.COMPLETE_INITIAL_PASSWORD, {
      newPassword,
    });
    return unwrapData<User>(response);
  },

  async logout(): Promise<void> {
    await http.post(AUTH_ENDPOINTS.LOGOUT);
  },

  // No request body — Strapi reads the refresh token from the HttpOnly cookie.
  async refreshToken(): Promise<RefreshTokenResponse> {
    return http.post<RefreshTokenResponse>(AUTH_ENDPOINTS.REFRESH);
  },

  async requestPasswordReset(email: string): Promise<void> {
    await http.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await http.post(AUTH_ENDPOINTS.RESET_PASSWORD, {
      token,
      password: newPassword,
    });
  },
};
